import React, { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { NumericInput } from "@/components/inputs";
import {
  AddressInput,
  CountrySelect,
  LoanTypeSelect,
  PropertyTypeSelect,
  PropertyUseSelect,
  StateSelect,
} from "@/modules/shared/components/inputs";
import { LOAN_TYPE_CODES } from "@/modules/loan/utils/loan-type-codes";
import useDealSummaryLocation from "@/modules/client/loan-application/features/loan-origination/sub-features/deal-summary/hooks/use-deal-summary-location";
import useLoanTypeCode from "@/modules/client/loan-application/features/loan-origination/sub-features/deal-summary/hooks/use-loan-type-code";
import RefinanceFields from "@/modules/client/loan-application/features/loan-origination/sub-features/deal-summary/components/refinance-fields";
import ConstructionFields from "@/modules/client/loan-application/features/loan-origination/sub-features/deal-summary/components/construction-fields";

const SCOPE = "details";

/**
 * DealSummaryStep — Step 1: collects core loan parameters.
 */
export default function DealSummaryStep() {
  const { control, setValue, formState: { errors } } = useFormContext();
  const stepErrors = errors?.details;
  const [allowUnits, setAllowUnits] = useState(false);
  const loanTypeCode = useLoanTypeCode(SCOPE);
  const { countryId, countryIsoCode, defaultCountryId, handleCountryChange, handlePlaceSelected } = useDealSummaryLocation(setValue, SCOPE);

  const isRefinance = loanTypeCode === LOAN_TYPE_CODES.REFINANCE;
  const isConstruction = loanTypeCode === LOAN_TYPE_CODES.CONSTRUCTION;

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-5">
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.loanTypeId`} control={control} render={({ field }) => (
          <LoanTypeSelect value={field.value} onChange={field.onChange} error={stepErrors?.loanTypeId?.message} />
        )} />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.propertyTypeId`} control={control} render={({ field }) => (
          <PropertyTypeSelect
            value={field.value}
            onChange={val => { field.onChange(val); }}
            onAllowUnitsChange={flag => { setAllowUnits(flag); if (!flag) setValue(`${SCOPE}.units`, null); }}
            error={stepErrors?.propertyTypeId?.message}
          />
        )} />
      </div>
      {allowUnits && (
        <div className="col-span-2 sm:col-span-1">
          <Controller name={`${SCOPE}.units`} control={control} render={({ field }) => (
            <NumericInput placeholder="Units" value={field.value} onChange={field.onChange} min={1} format="n0" error={stepErrors?.units?.message} />
          )} />
        </div>
      )}
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.propertyUseTypeId`} control={control} render={({ field }) => (
          <PropertyUseSelect value={field.value} onChange={field.onChange} error={stepErrors?.propertyUseTypeId?.message} />
        )} />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.address`} control={control} render={({ field }) => (
          <AddressInput placeholder="Address *" value={field.value} onChange={field.onChange} countryIsoCode={countryIsoCode} onPlaceSelected={handlePlaceSelected} error={stepErrors?.address?.message} />
        )} />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.stateId`} control={control} render={({ field }) => (
          <StateSelect value={field.value} onChange={field.onChange} countryId={countryId} error={stepErrors?.stateId?.message} />
        )} />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.countryId`} control={control} render={({ field }) => (
          <CountrySelect value={field.value || defaultCountryId} onChange={item => handleCountryChange(item, field.onChange)} error={stepErrors?.countryId?.message} />
        )} />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.amount`} control={control} render={({ field }) => (
          <NumericInput placeholder="Loan Amount *" value={field.value} onChange={field.onChange} format="c0" currency="USD" min={0} error={stepErrors?.amount?.message} />
        )} />
      </div>
      {isRefinance && <RefinanceFields scope={SCOPE} />}
      {isConstruction && <ConstructionFields scope={SCOPE} />}
      <div className="col-span-2 sm:col-span-1">
        <Controller name={`${SCOPE}.estimatedValue`} control={control} render={({ field }) => (
          <NumericInput
            placeholder={isConstruction ? "Value at Completion / After Repair Value *" : "Estimated Value *"}
            value={field.value}
            onChange={field.onChange}
            format="c0"
            currency="USD"
            min={0}
            error={stepErrors?.estimatedValue?.message}
          />
        )} />
      </div>
    </div>
  );
}
